import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import NewEntryPage from './pages/NewEntryPage';
import EntriesPage from './pages/EntriesPage';
import EntryDetailPage from './pages/EntryDetailPage';
import RemindersPage from './pages/RemindersPage';
import PartnersPage from './pages/PartnersPage';
import SettingsPage from './pages/SettingsPage';
import PricingPage from './pages/PricingPage';
import ResourcesPage from './pages/ResourcesPage';
import InsightsPage from './pages/InsightsPage';
import AdminPage from './pages/AdminPage';
// Family Pages
import FamilyPricingPage from './pages/FamilyPricingPage';
import FamilyRegisterPage from './pages/FamilyRegisterPage';
import ChildRegisterPage from './pages/ChildRegisterPage';
import ParentDashboardPage from './pages/ParentDashboardPage';
import SecondParentJoinPage from './pages/SecondParentJoinPage';
import ChildRewardsPage from './pages/ChildRewardsPage';
import AdultRewardsPage from './pages/AdultRewardsPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-muted-foreground animate-pulse">Loading...</div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    return children;
};

// Public Route (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-muted-foreground animate-pulse">Loading...</div>
            </div>
        );
    }
    
    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }
    
    return children;
};

const AppRoutes = () => {
    return (
        <Routes>
            {/* Public Routes */}
            <Route 
                path="/" 
                element={
                    <PublicRoute>
                        <LandingPage />
                    </PublicRoute>
                } 
            />
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <AuthPage isLogin={true} />
                    </PublicRoute>
                } 
            />
            <Route 
                path="/register" 
                element={
                    <PublicRoute>
                        <AuthPage isLogin={false} />
                    </PublicRoute>
                } 
            />

            {/* Protected Routes */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/new-entry" 
                element={
                    <ProtectedRoute>
                        <NewEntryPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/entries" 
                element={
                    <ProtectedRoute>
                        <EntriesPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/entries/:id" 
                element={
                    <ProtectedRoute>
                        <EntryDetailPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/insights" 
                element={
                    <ProtectedRoute>
                        <InsightsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reminders" 
                element={
                    <ProtectedRoute>
                        <RemindersPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/partners" 
                element={
                    <ProtectedRoute>
                        <PartnersPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/resources" 
                element={
                    <ProtectedRoute>
                        <ResourcesPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/pricing" 
                element={
                    <ProtectedRoute>
                        <PricingPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/settings" 
                element={
                    <ProtectedRoute>
                        <SettingsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin" 
                element={
                    <ProtectedRoute>
                        <AdminPage />
                    </ProtectedRoute>
                } 
            />

            {/* Family Plan Routes */}
            <Route path="/family/pricing" element={<FamilyPricingPage />} />
            <Route path="/family/register" element={<FamilyRegisterPage />} />
            <Route path="/family/child-register" element={<ChildRegisterPage />} />
            <Route path="/family/join" element={<SecondParentJoinPage />} />
            <Route 
                path="/family/dashboard" 
                element={
                    <ProtectedRoute>
                        <ParentDashboardPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rewards" 
                element={
                    <ProtectedRoute>
                        <ChildRewardsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/my-rewards" 
                element={
                    <ProtectedRoute>
                        <AdultRewardsPage />
                    </ProtectedRoute>
                } 
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <ThemeProvider>
            <LanguageProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <div className="App">
                            <AppRoutes />
                            <Toaster position="top-right" richColors />
                        </div>
                    </BrowserRouter>
                </AuthProvider>
            </LanguageProvider>
        </ThemeProvider>
    );
}

export default App;
